import Text from "../../../Text";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog.tsx";
import { useEffect, type FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form.tsx";
import { Input } from "../../../ui/input.tsx";
import { Button } from "../../../ui/button.tsx";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Link, useLocation, useNavigate } from "react-router";
import {
  confirmByEmailToResetPassword,
  resetPasswordState,
  resetResetPasswordState,
  sendCodeByEmailToResetPassword,
} from "@/store/resetPasswordState.ts";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const formEmailSchema = z.object({
  email: z.email({ error: "Email invalide" }),
});

const formResetPasswordSchema = z.object({
  password: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      {
        error: "Mot de passe invalide",
      },
    ),
  code: z.string().min(6),
});

const regexUppercase = /^(?=.*[A-Z])[A-Za-z\d@$!%*?&]{1,}$/;
const regex8Char = /^[A-Za-z\d@$!%*?&]{8,}$/;
const regexNumber = /^(?=.*\d)[A-Za-z\d@$!%*?&]{1,}$/;
const regexSymbol = /^(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{1,}$/;

const getMessageError = (
  userError: Error,
  t: TFunction<"translation", undefined>,
): string => {
  if (["NetworkError"].includes(userError.message.split(":")[0]))
    return t("network_error");

  if (userError.message === "username is required to signUp")
    return t("email_required");

  if (userError.message === "Username/client id combination not found.")
    return t("account_not_found");

  if (
    userError.message ===
    "UserNotFoundException: Username/client id combination not found."
  )
    return t("account_not_found");

  if (
    userError.message ===
    "CodeMismatchException: Invalid verification code provided, please try again."
  )
    return t("invalid_code");

  return userError.message;
};

const ResetPassword: FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { error, passwordChanged } = resetPasswordState();

  const transformedPathname = pathname.replace("/reset-password", "");

  const formEmail = useForm<z.infer<typeof formEmailSchema>>({
    resolver: zodResolver(formEmailSchema),
    defaultValues: {
      email: "",
    },
  });

  const formResetPassword = useForm<z.infer<typeof formResetPasswordSchema>>({
    resolver: zodResolver(formResetPasswordSchema),
    defaultValues: {
      password: "",
      code: "",
    },
  });

  const handleSubmitEmail = (values: z.infer<typeof formEmailSchema>) => {
    sendCodeByEmailToResetPassword(values);
  };

  const handleSubmitPassword = (
    values: z.infer<typeof formResetPasswordSchema>,
  ) => {
    confirmByEmailToResetPassword({
      ...values,
      email: formEmail.getValues("email"),
    });
  };

  useEffect(() => {
    resetResetPasswordState();
    return () => {
      resetResetPasswordState();
    };
  }, []);

  useEffect(() => {
    if (passwordChanged) {
      resetResetPasswordState();
      navigate(`${transformedPathname}/sign-in`);
    }
  }, [passwordChanged, transformedPathname]);

  return (
    <DialogContent showCloseButton={false}>
      <VisuallyHidden>
        <DialogTitle>Reset password</DialogTitle>
      </VisuallyHidden>
      <DialogHeader>
        <VisuallyHidden>
          <DialogDescription>Reset password here</DialogDescription>
        </VisuallyHidden>
        <img
          src="/svg/krunsh-logo.svg"
          alt="krunsh logo"
          height={36}
          width={120}
        />
        <Text fontWeight={600} fontSize={20} color="#44546F">
          {t("reset_password_title")}
        </Text>
        <Text fontWeight={400} fontSize={14} color="#44546F">
          {t("reset_password_subtitle")}
        </Text>
      </DialogHeader>
      <Form {...formEmail}>
        <form onSubmit={formEmail.handleSubmit(handleSubmitEmail)}>
          <div className="gap-[12px] flex flex-col">
            <FormField
              control={formEmail.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("email")}</FormLabel>
                  <FormControl>
                    <Input placeholder="johndoe@gmail.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button className="mt-[8px]">{t("send_code")}</Button>
        </form>
      </Form>
      <hr />
      <Form {...formResetPassword}>
        <form onSubmit={formResetPassword.handleSubmit(handleSubmitPassword)}>
          <div className="gap-[12px] flex flex-col">
            <FormField
              control={formResetPassword.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputOTP maxLength={6} {...field}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormDescription>
                    {t("reset_password_code_description")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={formResetPassword.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("password")}</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="*********" {...field} />
                  </FormControl>
                  <FormDescription>
                    <span
                      style={{
                        color: regex8Char.test(field.value) ? "#00A408" : "",
                      }}
                    >
                      {t("password_requirements_size")}
                    </span>
                    <span
                      style={{
                        color: regexUppercase.test(field.value)
                          ? "#00A408"
                          : "",
                      }}
                    >{` ${t("password_requirements_uppercase")},`}</span>
                    <span
                      style={{
                        color: regexNumber.test(field.value) ? "#00A408" : "",
                      }}
                    >{` ${t("password_requirements_number")}`}</span>
                    <span
                      style={{
                        color: regexSymbol.test(field.value) ? "#00A408" : "",
                      }}
                    >
                      {` ${t("password_requirements_symbol")}`}
                    </span>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex justify-between mt-[24px] gap-[16px] w-[100%]">
            <Link to={`${transformedPathname}/sign-in`} className="w-[30%]">
              <Button variant="secondaryFilled" className="w-full">
                {t("back")}
              </Button>
            </Link>
            <Button
              type="submit"
              variant="primaryFilled"
              style={{ width: "calc(70% - 16px)" }}
              disabled={
                !formEmail.formState.isValid ||
                !formResetPassword.formState.isValid
              }
            >
              {t("reset_password_button")}
            </Button>
          </div>
        </form>
      </Form>
      {error && (
        <Alert variant="destructive">
          <AlertTitle>{t("reset_password_error_title")}</AlertTitle>
          <AlertDescription>{getMessageError(error, t)}</AlertDescription>
        </Alert>
      )}
    </DialogContent>
  );
};

export default ResetPassword;
